import React, { useState } from 'react';
import {
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Stack,
  Paper,
  Tooltip,
  styled,
  alpha,
  Avatar,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Reply as ReplyIcon,
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  OpenInNew as OpenInNewIcon,
  Task as TaskIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Message, Agent } from '../../stores/chatStore';
import { useChatStore } from '../../stores/chatStore';
import AgentAvatar from './AgentAvatar';
import MessageRenderer from './MessageRenderer';
import ReactionPicker from './ReactionPicker';
import PanelOpener from './PanelOpener';
import SaveToApp from './SaveToApp';
import AddToGraphButton from './AddToGraphButton';
import ViewInGraphButton from './ViewInGraphButton';
import CitationsDropdown, { Citation } from './CitationsDropdown';
import TopicPills from './TopicPills';
import { useFeatureFlag } from '../../hooks/useFeatureFlags';
import { isHtmlContent } from '../../utils/htmlSanitizer';
import { copyFormattedContent, copyPlainText } from '../../utils/copyFormatted';

interface MessageItemProps {
  message: Message;
  agent?: Agent;
  currentUserId: string;
  isHighlighted?: boolean;  // NEW: For deep linking from Graph
  conversationType?: 'ai_chat' | 'dm' | 'group' | 'channel';
  onEdit?: (messageId: string, content: string) => void;
  onReply?: (messageId: string) => void;
  onReact?: (messageId: string, emoji: string) => void;
  onRemoveReaction?: (messageId: string, emoji: string) => void;
  onOpenPanel?: (panelType: string, metadata: any) => void;
  showThread?: boolean;
}

const MessageContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isUser' && prop !== 'isHighlighted'
})<{ isUser?: boolean; isHighlighted?: boolean }>(({ theme, isUser, isHighlighted }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  backgroundColor: isHighlighted
    ? alpha(theme.palette.warning.main, 0.15)  // Gold highlight for deep links
    : isUser
    ? alpha(theme.palette.primary.main, 0.08)
    : theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.3s ease',
  maxWidth: '70%',
  marginLeft: isUser ? 'auto' : 0,
  marginRight: isUser ? 0 : 'auto',
  border: isHighlighted ? `2px solid ${theme.palette.warning.main}` : 'none',
  '&:hover': {
    backgroundColor: isHighlighted
      ? alpha(theme.palette.warning.main, 0.2)
      : isUser
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.action.hover, 0.05),
    boxShadow: theme.shadows[2],
  },
}));

const MessageHeader = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isUser'
})<{ isUser?: boolean }>(({ theme, isUser }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  flexDirection: isUser ? 'row-reverse' : 'row',
}));

const MessageContent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isUser'
})<{ isUser?: boolean }>(({ theme, isUser }) => ({
  paddingLeft: isUser ? theme.spacing(2) : theme.spacing(6),
  paddingRight: isUser ? theme.spacing(2) : 0,
  position: 'relative',
  textAlign: 'left',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  minWidth: 0,
}));

const ReactionChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontSize: '0.875rem',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const ThreadIndicator = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  paddingLeft: theme.spacing(6),
  color: theme.palette.primary.main,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  agent,
  currentUserId,
  isHighlighted = false,
  conversationType = 'ai_chat',
  onEdit,
  onReply,
  onReact,
  onRemoveReaction,
  onOpenPanel,
  showThread = false,
}) => {
  const { showHtmlRenderer, detectTasksFromMessage, isDetectingTasks } = useChatStore();
  const topicPillsEnabled = useFeatureFlag('topicPills');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showRawOverride, setShowRawOverride] = useState<boolean | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Determine if we should show raw based on global setting and local override
  // showHtmlRenderer true = render HTML, false = show raw
  const showRaw = showRawOverride !== null ? showRawOverride : !showHtmlRenderer;

  const isUserMessage = message.role === 'user';
  const isMultiParticipant = conversationType !== 'ai_chat';
  const canEdit = isUserMessage && onEdit;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCopy = async () => {
    handleMenuClose();

    // Check if content is HTML
    const hasHtml = message.content_type === 'html' || isHtmlContent(message.content);

    try {
      let success = false;

      if (hasHtml) {
        // Copy with formatting preserved (like copying from a PDF)
        success = await copyFormattedContent(message.content);
      } else {
        // Copy as plain text
        success = await copyPlainText(message.content);
      }

      if (success) {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    } catch (error) {
      console.error('Copy failed:', error);
      // Fallback to simple text copy
      try {
        await navigator.clipboard.writeText(message.content);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);
      }
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    handleMenuClose();
  };

  const handleSaveEdit = () => {
    if (onEdit && editContent.trim() !== message.content) {
      onEdit(message.id, editContent.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditContent(message.content);
    setIsEditing(false);
  };

  const handleOpenHtmlInNewTab = () => {
    // Create a new window with the HTML content
    const htmlContent = message.content;
    const newWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (newWindow) {
      // Write the HTML content to the new window
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>HTML Preview - ${message.id}</title>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
              padding: 20px;
              max-width: 1200px;
              margin: 0 auto;
            }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  const handleDetectTasks = async () => {
    handleMenuClose();
    await detectTasksFromMessage(message.id);
  };

  const handleReaction = (emoji: string) => {
    if (onReact) {
      const existingReaction = message.reactions?.find(
        (r) => r.user_id === currentUserId && r.emoji === emoji
      );
      if (existingReaction && onRemoveReaction) {
        onRemoveReaction(message.id, emoji);
      } else {
        onReact(message.id, emoji);
      }
    }
  };


  const groupedReactions = message.reactions?.reduce((acc, reaction) => {
    if (!acc[reaction.emoji]) {
      acc[reaction.emoji] = { count: 0, userReacted: false };
    }
    acc[reaction.emoji].count++;
    if (reaction.user_id === currentUserId) {
      acc[reaction.emoji].userReacted = true;
    }
    return acc;
  }, {} as Record<string, { count: number; userReacted: boolean }>);

  return (
    <MessageContainer
      id={`message-${message.id}`}
      elevation={0}
      isUser={isUserMessage}
      isHighlighted={isHighlighted}
    >
      <MessageHeader isUser={isUserMessage}>
        {agent ? (
          <AgentAvatar agent={agent} showStatus />
        ) : isMultiParticipant && isUserMessage ? (
          <Avatar style={{ width: 40, height: 40, backgroundColor: message.sender_id === currentUserId ? '#1976d2' : '#9c27b0' }}>
            {(message.sender_name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
          </Avatar>
        ) : (
          <Avatar style={{ width: 40, height: 40 }}>U</Avatar>
        )}
        <div style={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {agent
              ? agent.custom_name || agent.name
              : isMultiParticipant && message.sender_name
                ? `${message.sender_name}${message.sender_id === currentUserId ? ' (you)' : ''}`
                : 'You'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(message.created_at), 'MMM d, yyyy HH:mm')}
            {message.edited_at && ' (edited)'}
          </Typography>
          {isDetectingTasks && !isUserMessage && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
              <CircularProgress size={16} color="primary" />
              <Typography variant="body2" color="primary" style={{ fontSize: '0.875rem' }}>
                Task detection running...
              </Typography>
            </div>
          )}
        </div>
        <Stack direction="row" spacing={0.5}>
          {/* Task Detection button - only for assistant messages */}
          {!isUserMessage && (
            <Tooltip title={isDetectingTasks ? "Processing task detection in background (1-2 minutes)..." : "Detect tasks from this message"}>
              <IconButton size="small" onClick={handleDetectTasks} disabled={isDetectingTasks}>
                {isDetectingTasks ? (
                  <CircularProgress size={16} color="primary" />
                ) : (
                  <TaskIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}

          {/* View in Knowledge Graph button */}
          <ViewInGraphButton message={message} />

          {/* Add to Knowledge Graph button */}
          <AddToGraphButton
            message={message}
            conversationId={message.conversation_id}
          />

          {/* Dynamic panel opener for HVAC, electrical, and other specialized panels */}
          <PanelOpener
            message={message}
            onOpenPanel={onOpenPanel}
          />
          
          {/* HTML-specific controls */}
          {(message.content_type === 'html' || isHtmlContent(message.content)) && (
            <>
              {/* Keep SaveToApp for generic HTML content that doesn't have a specialized panel */}
              {!message.metadata?.hvacCalculationId && !message.metadata?.electricalCalculationId && !message.metadata?.mepCoordinationId && (
                <SaveToApp 
                  message={message} 
                  conversationId={message.conversation_id}
                  onSaved={() => {}}
                />
              )}
              <Tooltip title={showRaw ? 'Show rendered' : 'Show raw HTML'}>
                <IconButton size="small" onClick={() => setShowRawOverride(showRaw ? false : true)}>
                  {showRaw ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Open HTML in new tab">
                <IconButton size="small" onClick={handleOpenHtmlInNewTab}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      </MessageHeader>

      <MessageContent isUser={isUserMessage}>
        {isEditing ? (
          <div>
            <TextField
              fullWidth
              multiline
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveEdit();
                }
                if (e.key === 'Escape') {
                  handleCancelEdit();
                }
              }}
              autoFocus
            />
            <Stack direction="row" spacing={1} mt={1}>
              <Button size="small" onClick={handleSaveEdit}>Save</Button>
              <Button size="small" onClick={handleCancelEdit}>Cancel</Button>
            </Stack>
          </div>
        ) : (
          <MessageRenderer message={message} showRaw={showRaw} />
        )}

        {/* Topic Pills - Subtle semantic enhancement */}
        <TopicPills
          messageId={message.id}
          compact={isUserMessage}
          maxVisible={isUserMessage ? 1 : 2}
          enabled={topicPillsEnabled}
          onTopicClick={(topic) => {
            // TODO: Implement topic-based filtering
            console.log('Topic clicked for future filtering:', topic);
          }}
        />

        {/* HVAC Calculation Indicator */}
        {message.metadata?.hvacCalculationId && message.metadata?.showHVACPanel && (
          <div style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: 'var(--mui-palette-primary-light, #90caf9)',
            color: 'var(--mui-palette-primary-contrastText, #fff)',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <Typography variant="body2">
              📊 HVAC calculation complete. The panel should open automatically →
            </Typography>
          </div>
        )}

        {/* Reactions */}
        {(message.reactions?.length || 0) > 0 && (
          <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" gap={0.5}>
            {Object.entries(groupedReactions || {}).map(([emoji, { count, userReacted }]) => (
              <ReactionChip
                key={emoji}
                label={`${emoji} ${count}`}
                size="small"
                color={userReacted ? 'primary' : 'default'}
                onClick={() => handleReaction(emoji)}
              />
            ))}
          </Stack>
        )}

        {/* Reaction picker */}
        <ReactionPicker onSelect={handleReaction} />

        {/* Citations dropdown */}
        {message.metadata?.citations && message.metadata.citations.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <CitationsDropdown
              citations={message.metadata.citations as Citation[]}
              defaultExpanded={false}
            />
          </div>
        )}
      </MessageContent>

      {/* Thread indicator */}
      {message.thread_messages && message.thread_messages.length > 0 && !showThread && (
        <ThreadIndicator onClick={() => onReply?.(message.id)}>
          <ReplyIcon fontSize="small" />
          <Typography variant="body2">
            {message.thread_messages.length} {message.thread_messages.length === 1 ? 'reply' : 'replies'}
          </Typography>
        </ThreadIndicator>
      )}

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" style={{ marginRight: 8 }} />
            Edit
          </MenuItem>
        )}
        {onReply && (
          <MenuItem onClick={() => { onReply(message.id); handleMenuClose(); }}>
            <ReplyIcon fontSize="small" style={{ marginRight: 8 }} />
            Reply in thread
          </MenuItem>
        )}
        <MenuItem onClick={handleCopy}>
          <CopyIcon fontSize="small" style={{ marginRight: 8 }} />
          Copy
        </MenuItem>
        {!isUserMessage && (
          <MenuItem onClick={handleDetectTasks} disabled={isDetectingTasks}>
            {isDetectingTasks ? (
              <CircularProgress size={16} color="primary" style={{ marginRight: 8 }} />
            ) : (
              <TaskIcon fontSize="small" style={{ marginRight: 8 }} />
            )}
            {isDetectingTasks ? 'Processing (1-2 min)...' : 'Detect Tasks'}
          </MenuItem>
        )}
        {(message.content_type === 'html' || isHtmlContent(message.content)) && (
          <MenuItem onClick={() => { setShowRawOverride(!showRaw); handleMenuClose(); }}>
            <CodeIcon fontSize="small" style={{ marginRight: 8 }} />
            {showRaw ? 'Show rendered' : 'Show source'}
          </MenuItem>
        )}
      </Menu>

      {/* Copy success notification */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" style={{ width: '100%' }}>
          Copied with formatting!
        </Alert>
      </Snackbar>
    </MessageContainer>
  );
};

export default MessageItem;